import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CButton, CCard } from "@coreui/react-pro";
import CIcon from "@coreui/icons-react";
import { cilCheckAlt, cilEqualizer, cilInfo, cilReload } from "@coreui/icons";
import { useTheme } from "@scryerlabs/ui";
import type { PortfolioTrackerDto, TrackerResultsDto } from "../../../api/hooks/usePortfolioTracker";
import {
  formatDay,
  formatDayRange,
  formatFall,
  formatMoney,
  formatPercent,
  formatSignedMoney,
  formatSignedPercent,
  strategyColours,
  toneOf,
} from "../portfolio-tracker";
import { ProfitByStrategy } from "./ProfitByStrategy";
import { StrategyTable } from "./StrategyTable";
import { TrackerTiles } from "./TrackerTiles";
import { ValueChart } from "./ValueChart";

interface TrackerResultsProps {
  tracker: PortfolioTrackerDto;
  results: TrackerResultsDto;
  onChange: () => void;
  onRestart: () => void;
}

/** What the saved setup has made: the figures, the value over time, and strategy by strategy. */
export function TrackerResults({ tracker, results, onChange, onRestart }: TrackerResultsProps) {
  const { t } = useTranslation("portfolioTracker");
  const { theme } = useTheme();
  const [hoverDay, setHoverDay] = useState<string | null>(null);

  const strategies = useMemo(
    () => new Map(tracker.strategies.map((strategy) => [strategy.tradingStrategyId, strategy])),
    [tracker.strategies],
  );
  const colours = useMemo(() => {
    const ramp = strategyColours(results.strategies.length, theme);
    return new Map(results.strategies.map((strategy, index) => [strategy.tradingStrategyId, ramp[index]]));
  }, [results.strategies, theme]);

  const count = results.strategies.length;
  const drawdown = results.drawdown;
  const fell = drawdown.percent >= 0.05 && drawdown.from !== null && drawdown.to !== null;
  const tiles = [
    {
      label: t("tiles.putIn"),
      value: formatMoney(results.putIn),
      sub:
        results.cashPercent > 0
          ? t("tiles.strategiesWithCash", { count, cash: formatPercent(results.cashPercent) })
          : t("tiles.strategies", { count }),
    },
    { label: t("tiles.worth"), value: formatMoney(results.worth), sub: t("tiles.on", { date: formatDay(tracker.asOf) }) },
    {
      label: t("tiles.profit"),
      value: formatSignedMoney(results.profit),
      sub: formatSignedPercent((results.profit / results.putIn) * 100),
      tone: toneOf(results.profit),
    },
    {
      label: t("tiles.drawdown"),
      value: formatFall(drawdown.percent),
      sub: fell
        ? drawdown.backOn
          ? t("tiles.fellBack", { range: formatDayRange(drawdown.from!, drawdown.to!), back: formatDay(drawdown.backOn) })
          : t("tiles.fellNotBack", { range: formatDayRange(drawdown.from!, drawdown.to!) })
        : t("tiles.noFall"),
      tone: fell ? "text-danger" : "",
    },
    {
      label: t("tiles.trades"),
      value: t("tally.closed", { count: results.closedTrades }),
      sub: [
        t("tally.wonLost", { won: results.won, lost: results.lost }),
        results.openTrades ? t("tally.open", { count: results.openTrades }) : null,
      ]
        .filter(Boolean)
        .join(" · "),
    },
  ];

  const leftOut = results.leftOut.map((strategy) => strategy.name);
  const notes = [
    results.unpricedTrades > 0 ? t("results.unpriced", { count: results.unpricedTrades }) : null,
    results.alertPricedTrades > 0 ? t("results.alertPriced", { count: results.alertPricedTrades }) : null,
    leftOut.length > 0
      ? t("results.leftOut", {
          count: leftOut.length,
          names:
            leftOut.length === 1
              ? leftOut[0]
              : t("results.list", { rest: leftOut.slice(0, -1).join(", "), last: leftOut[leftOut.length - 1] }),
        })
      : null,
  ].filter((note): note is string => note !== null);

  return (
    <div className="tracker-results">
      <CCard className="tracker-summary">
        <div className="tracker-summary-top">
          <div>
            <div className="tracker-eyebrow">{t("results.eyebrow")}</div>
            <h2 className="h5 mb-0">
              {t("results.period", { from: formatDay(results.firstStart), to: formatDay(tracker.asOf) })}
            </h2>
            <p className="tracker-saved">
              <CIcon icon={cilCheckAlt} size="sm" />
              {t("results.saved")}
            </p>
          </div>
          <div className="tracker-summary-actions">
            <CButton color="primary" variant="outline" onClick={onChange}>
              <CIcon icon={cilEqualizer} className="me-2" />
              {t("results.change")}
            </CButton>
            <CButton color="primary" variant="outline" onClick={onRestart}>
              <CIcon icon={cilReload} className="me-2" />
              {t("results.restart")}
            </CButton>
          </div>
        </div>
        <TrackerTiles tiles={tiles} />
        {notes.length > 0 && (
          <ul className="tracker-notes">
            {notes.map((note) => (
              <li key={note}>
                <CIcon icon={cilInfo} size="sm" />
                <span>{note}</span>
              </li>
            ))}
          </ul>
        )}
      </CCard>

      <div className="tracker-charts">
        <ValueChart
          results={results}
          asOf={tracker.asOf}
          colours={colours}
          hoverDay={hoverDay}
          onHover={setHoverDay}
        />
        <ProfitByStrategy
          results={results}
          strategies={strategies}
          colours={colours}
          day={hoverDay ?? tracker.asOf}
          asOf={tracker.asOf}
        />
      </div>

      <StrategyTable results={results} strategies={strategies} />

      <p className="tracker-fine mb-0">{t("results.fine")}</p>
    </div>
  );
}
